import { Title } from '@app/components/core/typography/Title'
import { useTranslations } from '@i18n/useLanguage'
import { Drawer } from '@app/components/blocks/drawer/Drawer'
import { Style } from 'inlines'
import { CheckboxGroup } from '@app/components/core/checkbox/CheckboxGroup'
import { ListRegionModel } from 'functions/app/geography/list/region'
import { useState, useEffect } from 'react'
import Row from '@app/components/layout/Row'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import Column from '@app/components/layout/Column'
import { ServiceProviderFilter } from 'functions/app/service-provider/list/filters'
import { useQuery } from '@based/sdk'
import { ServiceProviderTypeListModel } from 'functions/app/service-provider-type/list'

export const ServiceProviderFilterDrawer = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
}: {
  isOpen: boolean
  onClose: () => void
  filters: ServiceProviderFilter
  onFiltersChange: (filters: ServiceProviderFilter) => void
}) => {
  const {
    data: types,
  }: {
    data?: ServiceProviderTypeListModel[]
  } = useQuery('list-service-provider-type')
  const { data: regions }: { data?: ListRegionModel[] } =
    useQuery('list-region')
  const t = useTranslations().app

  const isMobile = useMediaQuery()

  const [selectedRegions, setSelectedRegions] = useState<number[]>([])
  const [selectedTypes, setSelectedTypes] = useState<number[]>([])

  useEffect(() => {
    if (isOpen) {
      setSelectedRegions(filters.regions ?? [])
      setSelectedTypes(filters.types ?? [])
    }
  }, [isOpen, filters.regions, filters.types])

  const toggleRegion = (region: number) => {
    setSelectedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region],
    )
  }

  const toggleType = (type: number) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  const handleApply = () => {
    onFiltersChange({
      regions: selectedRegions,
      types: selectedTypes,
    })
    onClose()
  }

  return (
    <Drawer
      title={t.discoverServiceProviders.filtersModal.title}
      open={isOpen}
      setOpen={onClose}
      side="left"
    >
      <Column
        style={{ height: '100%', flex: 0, justifyContent: 'space-between' }}
      >
        <div>
          <Title.H5 style={{ marginBottom: '1rem' }}>
            {t.discoverServiceProviders.filtersModal.type}
          </Title.H5>

          <TypesCheckboxGroup
            selected={selectedTypes}
            onToggle={toggleType}
            style={{ marginBottom: '1.5rem' }}
            items={types || []}
          />

          <Title.H5 style={{ marginBottom: '1rem' }}>
            {t.discoverServiceProviders.filtersModal.operatingArea}
          </Title.H5>

          <RegionCheckboxGroup
            selected={selectedRegions}
            onToggle={toggleRegion}
            items={regions || []}
          />
        </div>

        <Row
          style={{
            width: '100%',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          <Button onClick={onClose} label={t.common.back} variant="secondary" />
          <Button
            onClick={handleApply}
            label={t.discoverServiceProviders.filtersModal.apply}
          />
        </Row>
      </Column>
    </Drawer>
  )
}

interface FilterCheckboxGroupProps {
  selected: number[]
  onToggle: (value: number) => void
  style?: Style
}

interface RegionCheckboxGroupProps extends FilterCheckboxGroupProps {
  items: ListRegionModel[]
}

const RegionCheckboxGroup = ({
  selected,
  onToggle,
  items,
}: RegionCheckboxGroupProps) => {
  return (
    <CheckboxGroup
      options={items?.map((region) => ({
        label: region.name,
        value: region.id,
        key: region.code,
      }))}
      selected={selected}
      onToggle={onToggle}
      style={{ marginBottom: '0.5rem' }}
    />
  )
}

interface TypesCheckboxGroupProps extends FilterCheckboxGroupProps {
  items: ServiceProviderTypeListModel[]
}

const TypesCheckboxGroup = ({
  items,
  selected,
  onToggle,
  style,
}: TypesCheckboxGroupProps) => {
  return (
    <CheckboxGroup
      options={items?.map((type) => ({
        label: type.name,
        value: type.id,
        key: type.id,
      }))}
      selected={selected}
      onToggle={onToggle}
      style={style}
    />
  )
}
